import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup/dist/yup';
import cn from 'classnames';

import { Button, Checkbox, Confirm, Input, Modal, Select, SubmissionButtons } from '@components/common';
import AcceptDeclineIconButtons from '@components/common/AcceptDeclineIconButtons';
import DynamicIconWrapper from '@components/common/DynamicIconWrapper/DynamicIconWrapper';
import SpinnerOverlay from '@components/common/SpinnerOverlay';

import { Icons } from '@icons/icons';

import { useConfigPresets } from '@hooks/crowdCells/useConfigPresets';
import { useCreateConfigPreset } from '@hooks/crowdCells/useCreateConfigPreset';
import { useDefaultConfigParameters } from '@hooks/crowdCells/useDefaultConfigParameters';
import { useDeleteConfigPreset } from '@hooks/crowdCells/useDeleteConfigPreset';
import { useEditConfigPreset } from '@hooks/crowdCells/useEditConfigPreset';
import { useMutateUpdateGNodeBConfigParameters } from '@hooks/crowdCells/useMutateUpdateGNodeBConfigParameters';
import { useQueryConfigFileView } from '@hooks/crowdCells/useQueryConfigFileView';

import { useAppDispatch } from '@store/store';

import { setSnackbar } from '@slices/common';

import { ICrowdCellsConfigPreset } from '@interfaces/crowdcell/crowdCells';
import {
	EditGNodeBConfigFormValues,
	IGNodeBConfigParameter,
	IGNodeBConfigParameterWithComment,
} from '@interfaces/crowdcell/gNodeBConfig';

import { EButtonType, ESnackbarTypes } from '@enums/common';
import { EGNodeBConfigParameters, EGNodeBParametersTypes } from '@enums/crowdCells';
import { ERevalidationMode, EValidationMode } from '@enums/validationMode';

import colors from '@constants/colors';
import { MODAL_WIDTH_455, MODAL_WIDTH_480, MODAL_WIDTH_580, modalCloseConfirm } from '@constants/modals';
import { regExpDecimalAndHyphen } from '@constants/regExp';
import { responseMessages } from '@constants/responseMessages';

import { IEditFrequencyBand } from './EditFrequencyBandModal/getSchema';
import { IPresetOptionProps } from './PresetsOptionComponent/PresetsOptionComponent';
import ConfigFileView from './ConfigFileView';
import CreateConfigPreset from './CreateConfigPreset';
import EditConfigPreset from './EditConfigPreset';
import EditFrequencyBandModal from './EditFrequencyBandModal';
import PresetsOptionComponent from './PresetsOptionComponent';
import PreviewConfigFile from './PreviewConfigFile';
import { getSchema } from './schema';

import styles from './EditConfiguration.module.scss';

interface IEditConfigurationProps {
	gNodeBConfigParameters: IGNodeBConfigParameterWithComment[];
	isLoadingGNodeBConfigParameters: boolean;
	isRestart: boolean;
	closeEditConfigurationModal: (isWithoutConfirm?: boolean) => void;
	setIsDirty: (isDirty: boolean) => void;
	crowdCellId: string;
	frequency: string;
}

const editIcon = () => Icons.editIcon({ height: 16, width: 16 });

export const confirmTextWithRestart = 'Are you sure you want to apply new values for the configuration and restart mobile service on Flex-5G?';
export const confirmTextWithoutRestart = 'Are you sure you want to apply new values for the configuration?';

const EditConfiguration = ({
	gNodeBConfigParameters,
	isLoadingGNodeBConfigParameters,
	isRestart,
	closeEditConfigurationModal,
	crowdCellId,
	setIsDirty,
	frequency,
}: IEditConfigurationProps) => {
	const dispatch = useAppDispatch();

	const [localConfigParameters, setLocalConfigParameters] = useState(gNodeBConfigParameters || []);
	const [localFreq, setLocalFreq] = useState(frequency);

	useEffect(() => {
		if (gNodeBConfigParameters?.length) setLocalConfigParameters(gNodeBConfigParameters || []);
	}, [gNodeBConfigParameters]);

	const [preset, setPreset] = useState<ICrowdCellsConfigPreset | null>(null);
	const [isEditMode, setIsEditMode] = useState(false);
	const [searchValue] = useState('');

	const [editingItem, setEditingItem] = useState<IGNodeBConfigParameter | undefined>();
	const [isEditModalOpen, setIsEditModalOpen] = useState(false);

	const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

	const [isCreatePresetModalOpen, setIsCreatePresetModalOpen] = useState(false);
	const [isCreatePresetCloseConfirmModalOpen, setIsCreatePresetCloseConfirmModalOpen] = useState(false);

	const [isCreateConfigPresetDirty, setIsCreateConfigPresetDirty] = useState(false);

	const [editingPreset, setEditingPreset] = useState<ICrowdCellsConfigPreset | null>(null);
	const [isEditConfigPresetDirty, setIsEditConfigPresetDirty] = useState(false);
	const [isEditConfigConfirmationModalOpen, setIsEditConfigConfirmationModalOpen] = useState(false);

	const [isConfigFileViewOpen, setIsConfigFileViewOpen] = useState(false);
	const [isPreviewConfigFileOpen, setIsPreviewConfigFileOpen] = useState(false);

	const { text } = useQueryConfigFileView({
		id: crowdCellId,
		type: EGNodeBParametersTypes.gnodeb,
	});

	const openConfigFileView = () => setIsConfigFileViewOpen(true);
	const closeConfigFileView = () => setIsConfigFileViewOpen(false);

	const openPreviewConfigFile = () => setIsPreviewConfigFileOpen(true);
	const closePreviewConfigFile = () => setIsPreviewConfigFileOpen(false);

	const openEditConfigConfirmationModal = () => setIsEditConfigConfirmationModalOpen(true);
	const closeEditConfigConfirmationModal = () => setIsEditConfigConfirmationModalOpen(false);

	const openCreatePresetModal = () => setIsCreatePresetModalOpen(true);

	const closeCreatePresetModal = () => {
		closeCreatePresetCloseConfirmModal();
		setIsCreatePresetModalOpen(false);
	};

	const openCreatePresetCloseConfirmModal = () => setIsCreatePresetCloseConfirmModalOpen(true);
	const closeCreatePresetCloseConfirmModal = () => setIsCreatePresetCloseConfirmModalOpen(false);

	const openConfirmModal = () => setIsConfirmModalOpen(true);

	const closeConfirmModal = () => setIsConfirmModalOpen(false);

	const onCreatedPreset = () => {
		closeCreatePresetModal();

		dispatch(setSnackbar({
			message: responseMessages.presetCreated,
			type: ESnackbarTypes.success,
		}));
	};

	const onEditedPreset = () => {
		closeEditPresetModal();

		dispatch(setSnackbar({
			message: `${editingPreset?.name} has been changed`,
			type: ESnackbarTypes.success,
		}));
	};

	const onDeletedPreset = () => {
		closeDeletePresetConfirmationModal();

		dispatch(setSnackbar({
			message: `Preset ${deletingPreset?.name} has been deleted`,
			type: ESnackbarTypes.success,
		}));

		setDeletingPreset(null);
	};

	const { createConfigPreset, isPresetCreating } = useCreateConfigPreset({ onSuccess: onCreatedPreset });
	const { deleteConfigPreset, isPresetDeleting } = useDeleteConfigPreset(onDeletedPreset);
	const { editConfigPreset, isPresetEditing } = useEditConfigPreset(onEditedPreset);

	const { handleUpdateGNodeBConfigParameters } = useMutateUpdateGNodeBConfigParameters();

	const nrArfcnParameter = localConfigParameters?.find(item => item.name === EGNodeBConfigParameters.NrArfcn);

	const { isLoadingPresets, presets } = useConfigPresets({ searchValue });

	const [isDeletePresetConfirmationModalOpen, setIsDeletePresetConfirmationModalOpen] = useState(false);
	const [deletingPreset, setDeletingPreset] = useState<ICrowdCellsConfigPreset | null>(null);

	const openDeletePresetConfirmationModal = () => setIsDeletePresetConfirmationModalOpen(true);
	const closeDeletePresetConfirmationModal = () => {
		setIsDeletePresetConfirmationModalOpen(false);
		setDeletingPreset(null);
	};

	const {
		validationRules,
		isLoading: isLoadingDefaultConfigParameters,
	} = useDefaultConfigParameters();

	const schema = useMemo(() => {
		return getSchema(editingItem?.name, validationRules);
	}, [editingItem?.name, validationRules]);

	const {
		register,
		handleSubmit,
		formState: { errors },
		setValue,
		reset,
		watch,
	} = useForm<EditGNodeBConfigFormValues>({
		mode: EValidationMode.all,
		reValidateMode: ERevalidationMode.onChange,
		resolver: yupResolver(schema),
		defaultValues: {
			parameter: nrArfcnParameter && +nrArfcnParameter.value,
			isRestart,
		},
	});

	useEffect(() => {
		if (preset) {
			const parameters = preset?.files[0].parameters;

			if (parameters) setLocalConfigParameters(parameters);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [preset?.id]);

	const exitEditMode = () => {
		setIsEditMode(false);
		setEditingItem(undefined);

		reset({
			parameter: nrArfcnParameter && +nrArfcnParameter.value,
			isRestart: isRestartChecked,
		});
	};

	const positiveHandler = (values: EditGNodeBConfigFormValues) => {
		setLocalConfigParameters(prevState => {
			return prevState.map(prevItem => {
				if (prevItem.name === editingItem?.name) {
					return {
						...prevItem,
						value: `${values.parameter}`,
					};
				}

				return prevItem;
			});
		});

		if (preset) setPreset(null);

		exitEditMode();
	};

	const isRestartChecked = watch('isRestart');

	const onChangeIsRestart = () => setValue('isRestart', !isRestartChecked);

	const onSave = () => {
		closeConfirmModal();

		if (isPreviewConfigFileOpen) closePreviewConfigFile();

		handleUpdateGNodeBConfigParameters({
			id: crowdCellId,
			isRestart: isRestartChecked,
			parameters: localConfigParameters,
			frequency: localFreq,
			type: EGNodeBParametersTypes.gnodeb,
		});

		setIsDirty(false);
		closeEditConfigurationModal(true);
	};

	const isAnyParameterChanged = !gNodeBConfigParameters.every(item => {
		const gNodeBConfigParameter = localConfigParameters.find(prevItem => prevItem.name === item.name);

		return gNodeBConfigParameter && gNodeBConfigParameter.value === item.value;
	});

	const isSaveButtonEnabled = isRestartChecked !== isRestart || isAnyParameterChanged;

	const handleEdit = useCallback((item: IGNodeBConfigParameterWithComment) => () => {
		setEditingItem(item);
		setValue('parameter', +item?.value);

		setLocalConfigParameters(prevState => prevState.map(prevItem => {
			if (prevItem.name === item.name) {
				return {
					...prevItem,
					comment: item.comment,
					value: item.value,
				};
			}

			setIsDirty(true);

			return prevItem;
		}));

	}, [setValue, setIsDirty]);

	const openEditModal = useCallback((item: IGNodeBConfigParameter) => () => {
		setIsEditModalOpen(true);
		setEditingItem(item);
	}, []);

	const closeEditModal = useCallback(() => {
		setIsEditModalOpen(false);
		setEditingItem(undefined);
	}, []);

	const handleDeletePreset = useCallback((preset: ICrowdCellsConfigPreset) => {
		setDeletingPreset(preset);
		openDeletePresetConfirmationModal();
	}, []);

	const openEditPresetModal = (preset: ICrowdCellsConfigPreset) => {
		setEditingPreset(preset);
	};

	const closeEditPresetModal = () => {
		setEditingPreset(null);
		closeEditConfigConfirmationModal();
	};

	const handleCreateConfigPreset = (values: { name: string; comment?: string }) => {
		createConfigPreset({
			...values,
			files: [
				{
					parameters: localConfigParameters,
					type: EGNodeBParametersTypes.gnodeb,
					frequency: localFreq,
				},
			],
		});
	};

	const onCloseEditPresetModalCloseConfirmModal = () => {
		if (isEditConfigPresetDirty) {
			openEditConfigConfirmationModal();
			return closeEditPresetModal();
		}

		closeEditConfigConfirmationModal();
		closeEditPresetModal();
	};

	const onCloseCreateConfigPresetModal = () => {
		if (isCreateConfigPresetDirty) {
			return openCreatePresetCloseConfirmModal();
		}

		closeCreatePresetModal();
	};

	const deleteConfigPresetConfirm = () => {
		if (deletingPreset?.id) deleteConfigPreset(deletingPreset.id);
	};

	const handleResetToInitialState = useCallback(() => {
		setPreset(null);

		setLocalConfigParameters(gNodeBConfigParameters);

		setLocalFreq(frequency);
	}, [gNodeBConfigParameters, frequency]);

	const editPreset = useCallback((values: { name: string; comment?: string }) => {
		if (!editingPreset) return;

		editConfigPreset({
			...editingPreset,
			name: values.name,
			comment: values?.comment,
		});
	}, [editConfigPreset, editingPreset]);

	const handleSetPreset = useCallback((presetValue: ICrowdCellsConfigPreset | null) => {
		if (!presetValue) {
			handleResetToInitialState();
			setLocalFreq(frequency);

			return;
		}

		if (presetValue.files[0].frequency) setLocalFreq(presetValue.files[0].frequency);

		setPreset({
			...presetValue,
			files: [
				{
					parameters: presetValue.files[0].parameters.map(item => {
						return {
							...localConfigParameters.find(prevItem => prevItem.name === item.name),
							...item,
						};
					}),
					type: EGNodeBParametersTypes.gnodeb,
				},
			],
		});
	}, [
		handleResetToInitialState,
		frequency,
		localConfigParameters,
	]);

	const handleEditParameters = useCallback((values: IEditFrequencyBand): void => {
		if (values.frequency) setLocalFreq(`${values.frequency}`);

		setLocalConfigParameters((prevState: IGNodeBConfigParameterWithComment[]) => {
			return prevState.map((item: IGNodeBConfigParameterWithComment): IGNodeBConfigParameterWithComment => {
				if (item.name.toLowerCase() === EGNodeBConfigParameters.NrArfcn.toLowerCase() && values.nrArfcn) {
					return {
						...item,
						value: values.nrArfcn,
					};
				}

				if (item.name.toLowerCase() === EGNodeBConfigParameters.NrBand.toLowerCase() && values.nrBand?.value) {
					return {
						...item,
						value: +values.nrBand.value.split('N')[1],
					};
				}

				return item;
			});
		});

		closeEditModal();
	}, [closeEditModal]);

	const isSavePresetDisabled = !!preset || isEditMode || !isSaveButtonEnabled;

	return (
		<div
			className={styles.configFormContainer}
			data-testid='edit-configuration'
		>
			{isPreviewConfigFileOpen
				? (
					<Modal
						isOpen={!!crowdCellId}
						onClose={closePreviewConfigFile}
						title='Preview'
						width='800px'
					>
						<PreviewConfigFile
							crowdCellId={crowdCellId}
							frequency={localFreq}
							isRestartChecked={isRestartChecked}
							onClose={closePreviewConfigFile}
							onSave={openConfirmModal}
							parameters={localConfigParameters}
						/>
					</Modal>

				)
				: (
					<>
						<div className='display-flex flex-row align-center justify-between mb-15'>
							<Checkbox
								checked={isRestartChecked}
								label='Restart mobile_service.sh'
								name='isRestart'
								onChange={onChangeIsRestart}
							/>
							<Button
								onClick={openConfigFileView}
								withoutBackground
							>
								View config. file content
							</Button>
							<Button
								disabled={isEditMode || !isSaveButtonEnabled}
								onClick={openPreviewConfigFile}
							>
								Preview changes
							</Button>
						</div>
						<div className='display-flex flex-row mb-15'>
							<Select
								customComponents={{
									Option: (props) => PresetsOptionComponent({
										...props,
										handleEdit: openEditPresetModal,
										handleDelete: handleDeletePreset,
									} as IPresetOptionProps<ICrowdCellsConfigPreset>),
								}}
								handleChange={handleSetPreset}
								isClearable
								isWithoutUseForms
								maxMenuHeight={400}
								name='preset'
								noOptionsMessage='No presets found'
								options={presets}
								placeholder='Select a preset'
								value={preset}
								width='340px'
							/>
							<Button
								className='chooseFileButton ml-15'
								disabled={isSavePresetDisabled}
								onClick={openCreatePresetModal}
							>
								{Icons.diskette({ isDisabled: isSavePresetDisabled })}
							</Button>
						</div>
						<div className={styles.inputContainer}>
							{localConfigParameters
								?.map((item: IGNodeBConfigParameter, i) => {
									const isEditingItem = item.name === editingItem?.name;
									const isCalculatorNeeded = item.isCondition;

									return isEditingItem && !isEditModalOpen
										? (
											<div
												key={`${item.value}${i}`}
												className='display-flex flex-column align-start'
											>
												<div className='display-flex flex-row align-start'>
													<span className={cn(styles.nodeKey, {
														[styles.editing]: isEditingItem,
													})}>
														{item.name}
													</span>
													<div className='display-flex flex-column align-start'>
														<div className='display-flex flex-row'>
															<Input
																className={styles.editNodeInput}
																errorMessage={errors?.parameter?.message}
																name='parameter'
																regexValidation={regExpDecimalAndHyphen}
																register={register}
																required
																setValue={setValue}
																value={item.value}
															/>
															<AcceptDeclineIconButtons
																negativeHandler={exitEditMode}
																positiveHandler={handleSubmit(positiveHandler)}
															/>
														</div>
														{item.hintText && (
															<div className='display-flex align-center fs-regular_12 mt-5'>
																<div className={styles.iconBox}>
																	{Icons.info({
																		innerSvgProps: { height: 16, width: 16 },
																	})}
																</div>
																<span className='ml-5'>{item.hintText}</span>
															</div>
														)}
													</div>
												</div>
											</div>
										) : (
											<div
												key={`${item.value}${i}`}
												className={styles.nodeContainer}
											>
												<span className={styles.nodeKey}>
													{item.name}
												</span>
												<span className={styles.nodeValue}>
													{item.value}
												</span>
												{isCalculatorNeeded
													? <DynamicIconWrapper
														icon={Icons.calculator}
														onClick={openEditModal(item)}
														tooltipLabel='Edit NR_ARFCN and/or NR_BAND'
													/>
													: <DynamicIconWrapper
														icon={editIcon}
														onClick={handleEdit(item)}
														tooltipLabel='Edit node'
													/>
												}
											</div>
										);
								})}
						</div>
						<div className={styles.buttonsContainer}>
							<Button
								disabled={isEditMode || !isAnyParameterChanged}
								icon={Icons.resetIcon(
									(isEditMode || !isAnyParameterChanged) ? { color: colors.gray40 } : undefined,
								)}
								onClick={handleResetToInitialState}
								text='Reset to initial'
								type={EButtonType.reset}
								withoutBackground
							/>
							<SubmissionButtons
								className={styles.submissionButtons}
								disabledPositive={isEditMode || !isSaveButtonEnabled}
								negativeHandler={closeEditConfigurationModal}
								negativeLabel='Cancel'
								positiveHandler={openConfirmModal}
								positiveLabel='Save'
							/>
						</div>
					</>
				)
			}
			<Modal
				isOpen={isEditModalOpen}
				onClose={closeEditModal}
				title='Edit NR_ARFCN and/or NR_BAND'
				width={MODAL_WIDTH_580}
			>
				<EditFrequencyBandModal
					defaultValues={{
						frequency: localFreq,
						nrArfcn: localConfigParameters.find(item =>
							item.name.toLowerCase() === EGNodeBConfigParameters.NrArfcn.toLowerCase())?.value,
						nrBand: `N${localConfigParameters.find(item =>
							item.name.toLowerCase() === EGNodeBConfigParameters.NrBand.toLowerCase())?.value}`,
					}}
					negativeHandler={closeEditModal}
					positiveHandler={handleEditParameters}
				/>
			</Modal>
			<Modal
				isOpen={isConfirmModalOpen}
				onClose={closeConfirmModal}
				title='Confirm action'
				width={MODAL_WIDTH_480}
			>
				<Confirm
					onClose={closeConfirmModal}
					onSubmit={onSave}
					question={isRestartChecked ? confirmTextWithRestart : confirmTextWithoutRestart}
				/>
			</Modal>
			<Modal
				isOpen={isCreatePresetModalOpen}
				onClose={onCloseCreateConfigPresetModal}
				title='Save as preset'
				width={MODAL_WIDTH_455}
			>
				<CreateConfigPreset
					cancel={onCloseCreateConfigPresetModal}
					isLoading={!!isPresetCreating}
					onSave={handleCreateConfigPreset}
					preset={null}
					presets={presets}
					setIsDirtyForm={setIsCreateConfigPresetDirty}
				/>
			</Modal>
			<Modal
				isOpen={isConfigFileViewOpen}
				onClose={closeConfigFileView}
				title='View config. file content'
				width={MODAL_WIDTH_580}
			>
				<ConfigFileView
					onClose={closeConfigFileView}
					text={text}
				/>
			</Modal>
			<Modal
				isOpen={isCreatePresetCloseConfirmModalOpen}
				onClose={closeCreatePresetCloseConfirmModal}
				title='Confirm action'
				width={MODAL_WIDTH_480}
			>
				<Confirm
					{...modalCloseConfirm}
					onSubmit={closeCreatePresetModal}
				/>
			</Modal>
			<Modal
				isOpen={isDeletePresetConfirmationModalOpen}
				onClose={closeDeletePresetConfirmationModal}
				title='Confirm action'
				width={MODAL_WIDTH_480}
			>
				<Confirm
					onSubmit={deleteConfigPresetConfirm}
					question='Are you sure you want to delete preset?'
				/>
			</Modal>
			<Modal
				height='600'
				isOpen={!!editingPreset}
				isScrollableContent
				onClose={onCloseEditPresetModalCloseConfirmModal}
				title='Edit preset'
				width={MODAL_WIDTH_455}
			>
				<EditConfigPreset
					cancel={onCloseEditPresetModalCloseConfirmModal}
					isLoading={isPresetEditing}
					onSave={editPreset}
					preset={editingPreset}
					presets={presets}
					setIsDirtyForm={setIsEditConfigPresetDirty}
				/>
			</Modal>
			<Modal
				isOpen={isEditConfigConfirmationModalOpen}
				onClose={closeEditConfigConfirmationModal}
				title='Confirm action'
				width={MODAL_WIDTH_480}
			>
				<Confirm
					{...modalCloseConfirm}
					onSubmit={closeEditPresetModal}
				/>
			</Modal>
			{(isLoadingGNodeBConfigParameters
				|| isLoadingPresets
				|| isPresetDeleting
				|| isLoadingDefaultConfigParameters)
				&& <SpinnerOverlay />
			}
		</div>
	);
};

export default memo(EditConfiguration);
